import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Paper,
  Pagination,
  useTheme
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SubscriptionCard from "./SubscriptionCard";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { ROUTE_PLANS } from "../../constants/routes";
import AnimatedContainer from "../common/AnimatedContainer";
import { useGetSubscriptionList } from "../../hooks/api/useSubscription";
import type { RootState } from "../../store/store";
import { planBundleForSubscription } from "./subscriptionPlanBundle";

/** Order within non-active bucket: cancelled-style statuses before expired, etc. */
const PAST_STATUS_SORT_ORDER: Record<string, number> = {
  CANCELLED: 0,
  EXPIRED: 1,
  PAST_DUE: 2
};

/** Same row when space allows; wrap stays centered. flex-grow 0 keeps cards grouped */
const subscriptionCardRowWrapSx = {
  display: "flex",
  flex: { xs: "0 1 100%", sm: "0 1 300px", md: "0 1 320px" },
  maxWidth: { xs: "100%", sm: 420 },
  minWidth: { xs: "100%", sm: 260 }
} as const;

const SubscriptionView: React.FC = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const subscriptionsPerPage = isLargeScreen ? 6 : isMediumScreen ? 4 : 2;

  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const { subscriptionList } = useSelector(
    (state: RootState) => state.subscriptions
  );

  const getSubscriptionListMutation = useGetSubscriptionList();
  const [page, setPage] = useState(1);

  useEffect(() => {
    getSubscriptionListMutation.mutateAsync({}).catch(() => {});
  }, []);

  const subscriptions = useMemo(
    () => subscriptionList ?? [],
    [subscriptionList]
  );

  const activeSubscriptions = useMemo(
    () =>
      subscriptions
        .filter((sub) => sub.status === "ACTIVE")
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ),
    [subscriptions]
  );
  const pendingSorted = useMemo(() => {
    return subscriptions
      .filter((sub) => sub.status === "PENDING")
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
  }, [subscriptions]);

  const pastSubscriptions = useMemo(() => {
    return subscriptions
      .filter((sub) => sub.status !== "ACTIVE" && sub.status !== "PENDING")
      .sort((a, b) => {
        const ao = PAST_STATUS_SORT_ORDER[a.status] ?? 50;
        const bo = PAST_STATUS_SORT_ORDER[b.status] ?? 50;
        if (ao !== bo) return ao - bo;
        return (
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      });
  }, [subscriptions]);

  const subscriptionsInDisplayOrder = useMemo(
    () => [...activeSubscriptions, ...pendingSorted, ...pastSubscriptions],
    [activeSubscriptions, pendingSorted, pastSubscriptions]
  );

  const pageCount = Math.ceil(
    subscriptionsInDisplayOrder.length / subscriptionsPerPage
  );

  useEffect(() => {
    setPage((current) => Math.min(current, Math.max(pageCount, 1)));
  }, [pageCount, subscriptionsPerPage]);

  const paginatedSubscriptions = useMemo(() => {
    const start = (page - 1) * subscriptionsPerPage;
    return subscriptionsInDisplayOrder.slice(
      start,
      start + subscriptionsPerPage
    );
  }, [subscriptionsInDisplayOrder, page, subscriptionsPerPage]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
        position: "relative",
        overflow: "hidden",
        py: 6,
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-30%",
          right: "-15%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)"
            },
            "50%": {
              transform: "translate(-30px, -30px) rotate(180deg)"
            }
          }
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-20%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)"
            },
            "50%": {
              transform: "translate(30px, 30px) rotate(-180deg)"
            }
          }
        }}
      />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 6,
            flexDirection: "column",
            gap: 2,
            textAlign: "center"
          }}
        >
          <AnimatedContainer
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Box>
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: {
                    xs: "1.25rem",
                    sm: "1.60rem",
                    md: "2.65rem",
                    lg: "2.75rem"
                  },
                  lineHeight: 1.3,
                  mb: 2,
                  color: "white",
                  letterSpacing: { xs: "0.01em", md: "0.02em" },
                  textShadow: "0 2px 10px rgba(0,0,0,0.2)"
                }}
              >
                {t("subscription-view-title", "Your subscriptions")}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                  lineHeight: 1.7,
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: 400
                }}
              >
                {t(
                  "subscription-view-subtitle",
                  "Manage your subscription and billing"
                )}
              </Typography>
            </Box>
          </AnimatedContainer>
        </Box>

        {activeSubscriptions.length === 0 &&
          pendingSorted.length === 0 &&
          pastSubscriptions.length > 0 && (
            <Box sx={{ maxWidth: 640, mx: "auto", mb: 4, textAlign: "center" }}>
              <AnimatedContainer
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(8px)"
                  }}
                >
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5 }}>
                    {t(
                      "subscription-no-active-title",
                      "You do not have an active subscription"
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2.5 }}
                  >
                    {t(
                      "subscription-no-active-description",
                      "Choose a plan to restore access to subscription features."
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() =>
                      navigate(
                        localizedPath(ROUTE_PLANS, language, defaultLang)
                      )
                    }
                    sx={{
                      backgroundColor: BRAND_COLOR,
                      "&:hover": { backgroundColor: BRAND_HOVER },
                      py: 1.25,
                      px: 4,
                      borderRadius: 2,
                      fontWeight: "bold"
                    }}
                  >
                    {t("subscription-choose-plan-button", "Choose a plan")}
                  </Button>
                </Paper>
              </AnimatedContainer>
            </Box>
          )}

        {/* All subscriptions: sorted active → pending → cancelled; one centered wrapping row */}
        {subscriptionsInDisplayOrder.length > 0 && (
          <Box sx={{ mb: 6, width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                justifyContent: "center",
                alignItems: "stretch",
                width: "100%"
              }}
            >
              {paginatedSubscriptions.map((subscription) => {
                const { planName, features, isPrimary } =
                  planBundleForSubscription(subscription);
                const allowCancel =
                  subscription.status === "ACTIVE" ||
                  subscription.status === "PENDING";

                return (
                  <Box
                    key={subscription.subscriptionId}
                    sx={{ ...subscriptionCardRowWrapSx }}
                  >
                    <SubscriptionCard
                      subscription={subscription}
                      planName={planName}
                      features={features}
                      isPrimary={isPrimary}
                      onCancel={
                        allowCancel
                          ? () =>
                              getSubscriptionListMutation
                                .mutateAsync({})
                                .catch(() => {})
                          : undefined
                      }
                    />
                  </Box>
                );
              })}
            </Box>
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "white",
                      borderColor: "rgba(255, 255, 255, 0.5)",
                      "&.Mui-selected": {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        color: BRAND_COLOR,
                        fontWeight: "bold"
                      },
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.15)"
                      }
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* No Subscriptions */}
        {activeSubscriptions.length === 0 &&
          pendingSorted.length === 0 &&
          pastSubscriptions.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                bgcolor: "background.paper",
                borderRadius: 3,
                boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                mb: 6
              }}
            >
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                {t("subscription-empty-title", "No subscriptions")}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {t(
                  "subscription-empty-description",
                  "Subscribe to a plan to unlock all features"
                )}
              </Typography>
              <Button
                variant="contained"
                onClick={() =>
                  navigate(localizedPath(ROUTE_PLANS, language, defaultLang))
                }
                sx={{
                  backgroundColor: BRAND_COLOR,
                  "&:hover": {
                    backgroundColor: BRAND_HOVER
                  },
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  fontWeight: "bold"
                }}
              >
                {t("subscription-browse-plans-button", "Browse plans")}
              </Button>
            </Box>
          )}
      </Container>
    </Box>
  );
};

export default SubscriptionView;
